import { useMutation } from '@apollo/client';

import { useLingui } from '@lingui/react';
import { ActivityType } from '@zeus/index';

import { useEffect, useState } from 'react';
import {
  DragDropContext,
  DropResult,
  Droppable,
  Draggable,
} from 'react-beautiful-dnd';
import { styled } from 'styled-components';
import { GQL_CREATE_TRACKING } from '~/gql/tracking/tracking';
import { GQL_UPDATE_TRACKING_THEMATIQUE } from '~/gql/tracking-thematique/tracking-thematique';
import useInactivityDetect from '~/helpers/hooks/activity-detect';
import useSoundAudio from '~/helpers/hooks/soundAudio';
import { useTimer } from '~/helpers/hooks/timer';
import useConnectionStore from '~/helpers/store/connection/connection';
import {
  changeActivityOrder,
  getStatusMultipleAnswers,
  // getStatus,
  parseTextToJSX,
} from '~/pages/components/game/activities-forms/utils';
import { SPAdviceItem, SDivParsedItem } from '~/pages/components/styles/styles';
import Button from '~/pages/components/ui/button';
import { DragDropItem } from '~/pages/components/ui/drag-drop-item';
import { FeedbackItem } from '~/pages/components/ui/feedback-item';
import { QuestionItem } from '~/pages/components/ui/question-item';
import { TActivity } from '~/utils/airtable/types/types'; // , TAnswer
import { renderFullPath } from '~/utils/common/utils';

type IProps = {
  currentActivity: TActivity;
  nextActivity: () => void;
};

function DragDrop({ currentActivity, nextActivity }: IProps) {
  const { i18n } = useLingui();
  const [answers, setAnswers] = useState<string[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);

  const { currentTrackingThematiqueId, setCurrentTrackings, currentTrackings } =
    useConnectionStore();
  const { playSound } = useSoundAudio();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const { time, start, stop, clear } = useTimer();

  const [updateTrackingThematique] = useMutation(
    GQL_UPDATE_TRACKING_THEMATIQUE
  );
  const [createTracking] = useMutation(GQL_CREATE_TRACKING);

  const isActive = useInactivityDetect({ timeout: 5000 });

  useEffect(() => {
    if (!isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive]);

  useEffect(() => {
    initAnswers();
    setSubmitted(false);
    start();
    if (currentTrackingThematiqueId) {
      //setCurrentActivityOrder(currentActivity.order);
      setCurrentTrackings(
        changeActivityOrder(
          currentTrackingThematiqueId,
          currentActivity.order,
          currentTrackings
        )
      );
    }
  }, [currentActivity]);

  useEffect(() => {
    if (submitted) {
      if (isResponseCorrect()) playSound('feedbackGood');
      else playSound('feedbackWrong');
    }
  }, [submitted]);

  function initAnswers() {
    setSelectedAnswers([]);
    if (currentActivity.answers) {
      const _answers = currentActivity.answers.map((el) => el.text);
      setAnswers(_answers);
    }
  }

  function handleClickValid() {
    setSubmitted(true);
    stop();
  }

  async function handleNext() {
    try {
      if (currentTrackingThematiqueId) {
        await updateTrackingThematique({
          variables: { id: currentTrackingThematiqueId, time: time },
        });

        await createTracking({
          variables: {
            trackingThematiqueId: currentTrackingThematiqueId,
            activityOrder: currentActivity.order,
            activityType: currentActivity.type as ActivityType,
            answers: selectedAnswers,
            isCorrect: isResponseCorrect(),
            time: time === 0 ? 1 : time,
          },
        });
      }
    } finally {
      clear();

      playSound('click');
      nextActivity();
    }
  }

  function isResponseCorrect(): boolean {
    const correctAnswers = currentActivity.answers?.filter(
      (el) => el.isCorrect
    );
    let isCorrect = true;
    for (const response of selectedAnswers) {
      if (
        !currentActivity.answers?.find((el) => el.text === response)?.isCorrect
      )
        isCorrect = false;
    }
    return isCorrect && correctAnswers?.length === selectedAnswers.length;
  }

  function onDragEnd(result: DropResult) {
    playSound('click');

    if (result.destination?.droppableId === 'droppable') {
      const { draggableId } = result;
      const _answers = [...answers];
      const _selectedAnswers = [...selectedAnswers];
      const index = _answers.indexOf(draggableId);

      if (index !== -1) {
        _answers.splice(index, 1);
        _selectedAnswers.push(draggableId);
      }

      setAnswers(_answers);
      setSelectedAnswers(_selectedAnswers);
    }
  }

  function onDragStart() {
    //window.navigator.vibrate(100);
  }

  return (
    <Container $background={renderFullPath(currentActivity.background)}>
      <SDivBlur $withBackground={submitted}>
        <SDivContent>
          <SDivQuestion>
            {submitted ? (
              <>
                <FeedbackItem
                  characterImg={renderFullPath(currentActivity.characterImg)}
                >
                  {isResponseCorrect() ? (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackCorrect ?? '')}
                    </SDivParsedItem>
                  ) : (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackIncorrect ?? '')}
                    </SDivParsedItem>
                  )}
                </FeedbackItem>
              </>
            ) : (
              <>
                <QuestionItem
                  text={currentActivity.text}
                  characterImg={renderFullPath(currentActivity.characterImg)}
                  advice={i18n._('activities.advice.drag.drop')}
                />
              </>
            )}
          </SDivQuestion>
          <SDivDragDrop $canDrop={!submitted}>
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <SelectedContainer
                    {...provided.droppableProps}
                    $empty={selectedAnswers.length === 0}
                    $isDraggingOver={snapshot.isDraggingOver}
                    ref={provided.innerRef}
                  >
                    {selectedAnswers.length === 0 ? (
                      <SPAdviceItem $color="#fff">
                        {i18n._('activities.advice.drag.drop.zone')}
                      </SPAdviceItem>
                    ) : (
                      <>
                        {selectedAnswers.map((selectedA) => (
                          <SelectedAnswerItem key={selectedA}>
                            <DragDropItem
                              status={getStatusMultipleAnswers(
                                selectedA,
                                currentActivity.answers ?? [],
                                selectedAnswers
                              )}
                              dragged
                              submitted={submitted}
                            >
                              {selectedA}
                            </DragDropItem>
                          </SelectedAnswerItem>
                        ))}
                      </>
                    )}
                    {provided.placeholder}
                  </SelectedContainer>
                )}
              </Droppable>
              <Droppable isDropDisabled droppableId="droppable-disabled">
                {(provided) => (
                  <AnswersContainer
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <>
                      {answers.map((answer, index) => (
                        <Draggable
                          draggableId={answer}
                          index={index}
                          key={answer}
                        >
                          {(providedDraggable) => (
                            <AnswerItem
                              ref={providedDraggable.innerRef}
                              {...providedDraggable.draggableProps}
                              {...providedDraggable.dragHandleProps}
                            >
                              <DragDropItem
                                status={getStatusMultipleAnswers(
                                  answer,
                                  currentActivity.answers ?? [],
                                  selectedAnswers
                                )}
                                submitted={submitted}
                              >
                                {answer}
                              </DragDropItem>
                            </AnswerItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </>
                  </AnswersContainer>
                )}
              </Droppable>
            </DragDropContext>
          </SDivDragDrop>
        </SDivContent>
        <SDivBtn>
          {!submitted && (
            <Button
              color="secondary"
              disabled={selectedAnswers.length === 0}
              margin="0 10px 0 0"
              onClick={() => {
                initAnswers();
              }}
            >
              {i18n._('actions.reset')}
            </Button>
          )}

          <Button
            color="primary"
            disabled={!submitted && selectedAnswers.length === 0}
            onClick={submitted ? handleNext : handleClickValid}
          >
            {submitted ? i18n._('actions.next') : i18n._('actions.valid')}
          </Button>
        </SDivBtn>
      </SDivBlur>
    </Container>
  );
}

export default DragDrop;

const AnswersContainer = styled.div`
  align-items: flex-start;
  background-color: #fff;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 0 0 45%;
  justify-content: center;
  padding: 20px 20px 0 20px;
  width: 100%;
`;

const SelectedContainer = styled.div<{
  $empty: boolean;
  $isDraggingOver: boolean;
}>`
  align-items: center;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  flex-grow: 1;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;
/*
 background-color: ${({ $isDraggingOver }) =>
    $isDraggingOver
      ? 'rgba(197, 191, 184, 0.8)'
      : 'rgba(201, 197, 193, 0.6)'};
box-shadow: inset 0 2px 4px 0 rgba(83, 102, 153, 0.6);
*/

const AnswerItem = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: 50%;
  padding: 5px;
  width: 50%;
`;

const SelectedAnswerItem = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: 50%;
  padding: 5px;
  width: 50%;
`;

const Container = styled.div<{ $background?: string }>`
  align-items: center;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
`;

//backdrop-filter: blur(10px);
const SDivContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 0 0 85%;
  width: 100%;
`;

const SDivBtn = styled.div`
  align-items: center;
  background-color: #ffff;
  display: flex;
  justify-content: center;
  flex: 0 0 15%;
  width: 100%;
`;

const SDivBlur = styled.div<{ $withBackground?: boolean }>`
  display: flex;
  background-color: ${({ $withBackground }) =>
    $withBackground ? '#56afdc80' : 'transparent'};
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

// const SBlur = styled.div`
//   align-items: center;
//   display: flex;

//   flex-wrap: wrap;
//   flex-direction: column;
//   justify-content: space-between;
//   position: relative;

//   backdrop-filter: blur(10px);

//   height: 100%;
//   width: 100%;
// `;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

// Container pour drag-drop
const SDivDragDrop = styled.div<{ $canDrop?: boolean }>`
  align-items: center;
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  width: 100%;
  pointer-events: ${({ $canDrop }) => ($canDrop ? 'auto' : 'none')};
  background-color: rgba(148, 144, 141, 0.8);
  box-shadow: inset 0 2px 4px 0 rgba(83, 102, 153, 0.6);
`;
